import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';

interface TableDef {
  id: string;
  label: string;
  file: string;
}

interface TableData {
  headers: string[];
  rows: string[][];
}

@Component({
  selector: 'app-data-catalogue',
  standalone: true,
  imports: [CommonModule, HttpClientModule],
  templateUrl: './data-catalogue.html',
  styleUrl: './data-catalogue.scss'
})
export class DataCatalogue implements OnInit {
  // Main tab: 'catalogue' or 'tables'
  activeMainTab: 'catalogue' | 'tables' = 'catalogue';

  // ---------- CATALOGUE TAB (Data_Catalogue.csv) ----------
  catalogueData: TableData | null = null;
  catalogueLoading = false;
  catalogueError: string | null = null;

  // ---------- TABLES TAB (Patient, Referral, etc.) ----------
  tables: TableDef[] = [
    { id: 'patient',            label: 'Patient',                file: 'assets/Patient.csv' },
    { id: 'patient-comorbid',   label: 'Patient Comorbidities',  file: 'assets/Patient_Comorbidities.csv' },
    { id: 'procedure',          label: 'Procedure',              file: 'assets/Procedure.csv' },
    { id: 'referral',           label: 'Referral',               file: 'assets/Referral.csv' },
    { id: 'triage',             label: 'Triage',                 file: 'assets/Triage.csv' },
    { id: 'waitlist-status',    label: 'Waitlist Status',        file: 'assets/Waitlist_Status.csv' },
    { id: 'encounter-history',  label: 'Encounter History',      file: 'assets/Encounter_History.csv' },
    { id: 'observation-scores', label: 'Observation Scores',     file: 'assets/Observation_Scores.csv' },
    { id: 'observations-loinc', label: 'Observations (LOINC)',   file: 'assets/Observations_LOINC.csv' },
    { id: 'quality-of-life',    label: 'Quality of Life',        file: 'assets/Quality_of_Life.csv' }
  ];

  selectedTable: TableDef | null = null;
  tableData: TableData | null = null;
  isLoading = false;
  error: string | null = null;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadCatalogue();

    if (this.tables.length > 0) {
      this.selectTable(this.tables[0]);
    }
  }

  // ---------- CATALOGUE TAB LOGIC ----------
  private loadCatalogue(): void {
    this.catalogueLoading = true;
    this.catalogueError = null;
    this.catalogueData = null;

    this.http.get('assets/Data_Catalogue.csv', { responseType: 'text' }).subscribe({
      next: (csv: string) => {
        this.catalogueData = this.parseCsv(csv);
        this.catalogueLoading = false;
      },
      error: (err) => {
        console.error('Error loading Data_Catalogue.csv', err);
        this.catalogueError = 'Unable to load data catalogue.';
        this.catalogueLoading = false;
      }
    });
  }

  // ---------- TABLES TAB LOGIC ----------
  selectTable(table: TableDef): void {
    if (!table) return;
    this.selectedTable = table;
    this.loadTable(table);
  }

  private loadTable(table: TableDef): void {
    this.isLoading = true;
    this.error = null;
    this.tableData = null;

    this.http.get(table.file, { responseType: 'text' }).subscribe({
      next: (csv: string) => {
        this.tableData = this.parseCsv(csv);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading CSV', err);
        this.error = `Unable to load ${table.label} table.`;
        this.isLoading = false;
      }
    });
  }

  // ---------- CSV PARSER (quote-safe) ----------
  private parseCsv(csv: string): TableData {
    const lines = csv
      .split(/\r?\n/)
      .filter(l => l.trim().length > 0);

    if (lines.length === 0) return { headers: [], rows: [] };

    const headers = this.splitCsvLine(lines[0]);
    const rows = lines.slice(1).map(line => this.splitCsvLine(line));

    return { headers, rows };
  }

  private splitCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];

      if (ch === '"') {
        // Handle escaped quotes ("")
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
        continue;
      }

      current += ch;
    }

    result.push(current.trim());
    return result;
  }
}

